const API_BASE_URL = '/api';

function authHeaders() {
  const token = localStorage.getItem('caisar_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function registerUser(email, password, name, inviteCode = '') {
  const res = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, name, invite_code: inviteCode }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.detail || 'Registrierung fehlgeschlagen');
  }
  return res.json();
}

export async function loginUser(email, password) {
  const res = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.detail || 'Login fehlgeschlagen');
  }
  return res.json();
}

export async function uploadAudio(audioBlob, sessionId = null) {
  const formData = new FormData();
  formData.append('file', audioBlob, 'audio.webm');
  if (sessionId) formData.append('session_id', sessionId);

  const response = await fetch(`${API_BASE_URL}/transcription/upload`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) throw new Error('Transcription failed');

  const data = await response.json();
  return { text: data.text, speaker: data.speaker || null };
}

export async function getEnrollmentStatus() {
  const response = await fetch(`${API_BASE_URL}/enrollment/status`);
  if (!response.ok) throw new Error('Enrollment status failed');
  return response.json();
}

export async function enrollSpeaker(audioBlob) {
  const formData = new FormData();
  formData.append('file', audioBlob, 'audio.webm');
  const response = await fetch(`${API_BASE_URL}/enrollment/record`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Enrollment failed');
  }
  return response.json();
}

export async function resetDoctorProfile() {
  const response = await fetch(`${API_BASE_URL}/enrollment/profile`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Reset failed');
  return response.json();
}

export async function uploadDocument(file, category = 'Leitlinie') {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);
  const res = await fetch(`${API_BASE_URL}/documents/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Upload fehlgeschlagen');
  }
  return res.json();
}

export async function listDocuments() {
  const res = await fetch(`${API_BASE_URL}/documents/list`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Liste konnte nicht geladen werden');
  return res.json();
}

export async function deleteDocument(sourceId) {
  const res = await fetch(`${API_BASE_URL}/documents/${sourceId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Löschen fehlgeschlagen');
  return res.json();
}

export async function getSummary(text, previousSummary = null, patientContext = null) {
  const body = { text };
  if (previousSummary) body.previous_summary = previousSummary;
  if (patientContext) body.patient_context = patientContext;
  const response = await fetch(`${API_BASE_URL}/llm/summarize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error('Summary generation failed');
  }

  const data = await response.json();
  return data.sections;
}

export async function processWithLLM(text, prompt = null, numDiagnoses = 3, currentSummary = null, patientContext = null) {
  const body = { text, prompt, num_diagnoses: numDiagnoses };
  if (currentSummary) body.current_summary = currentSummary;
  if (patientContext) body.patient_context = patientContext;
  const response = await fetch(`${API_BASE_URL}/llm/process`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error('LLM processing failed');
  }

  const data = await response.json();
  return data.diagnoses;
}

export async function cleanupText(text) {
  const res = await fetch(`${API_BASE_URL}/llm/cleanup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error('Cleanup fehlgeschlagen');
  const data = await res.json();
  return data.text;
}

export async function checkInteractions(currentMedications, proposedMedications = []) {
  const res = await fetch(`${API_BASE_URL}/interactions/check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      current_medications: currentMedications,
      proposed_medications: proposedMedications,
    }),
  });
  if (!res.ok) throw new Error('Wechselwirkungsprüfung fehlgeschlagen');
  return res.json();
}

export async function askCDSQuestion(question, transcript, summaryContext, diagnosesContext, patientContext, history) {
  const res = await fetch(`${API_BASE_URL}/llm/cds-chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      question,
      transcript: transcript || null,
      summary_context: summaryContext || null,
      diagnoses_context: diagnosesContext || null,
      patient_context: patientContext || null,
      history: history || [],
    }),
  });
  if (!res.ok) throw new Error('CDS-Anfrage fehlgeschlagen');
  const data = await res.json();
  return data.answer;
}

export async function generateLearningNugget(topic, context) {
  const response = await fetch(`${API_BASE_URL}/llm/learn`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ topic, context }),
  });

  if (!response.ok) {
    throw new Error('Learning nugget generation failed');
  }

  const data = await response.json();
  return data.content;
}

// ── Patienten & Session-Persistenz ─────────────────────────────────────────

export async function fetchPatients() {
  const res = await fetch(`${API_BASE_URL}/patients`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Patienten laden fehlgeschlagen');
  return res.json();
}

export async function fetchPatientHistory(patientLabel) {
  const res = await fetch(`${API_BASE_URL}/patients/${encodeURIComponent(patientLabel)}/history`, {
    headers: authHeaders(),
  });
  if (!res.ok) return [];
  return res.json();
}

export async function archiveSessionToBackend(patientLabel, sessionName, summary, diagnoses, retentionDays, privateNotes, uploadedDocs) {
  const res = await fetch(`${API_BASE_URL}/patients/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      patient_label: patientLabel,
      session_name: sessionName,
      summary: summary || [],
      diagnoses: diagnoses || [],
      retention_days: retentionDays,
      private_notes: privateNotes || '',
      uploaded_docs: (uploadedDocs || []).map(({ id, name, size, category, status, content }) => ({ id, name, size, category, status, content: content || null })),
    }),
  });
  if (!res.ok) throw new Error('Session-Archivierung fehlgeschlagen');
  return res.json();
}

export async function fetchArchivedSessions(patientLabel = null) {
  const url = patientLabel
    ? `${API_BASE_URL}/patients/sessions?patient_label=${encodeURIComponent(patientLabel)}`
    : `${API_BASE_URL}/patients/sessions`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error('Sessions laden fehlgeschlagen');
  return res.json();
}

export async function deleteArchivedSession(sessionId) {
  const res = await fetch(`${API_BASE_URL}/patients/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Löschen fehlgeschlagen');
  return res.json();
}
